// Comparison Tab Component - Extracted from CalculatorPage.js
import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Trash2, AlertCircle, GitCompare, Download } from "lucide-react";
import { calculateOurOfferMetrics, computeFSIBreakdownFromFormData, computeStandaloneProjectMetrics } from "@/utils/calculations";
import axios from "axios";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { toast } from "sonner";
applyPlugin(jsPDF);

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Calculate competitor metrics for comparison
 * Mirrors calculateOurOfferMetrics logic so identical parameters give identical results
 */
const calculateCompetitorMetrics = (competitor, formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts) => {
  const conversionFactor = 0.092903;
  const totalFSIArea = fsiBreakdown.totalFSIWithAncillary || 0;
  const carpetFactor = parseFloat(formData.carpetAreaFactor) || 70;
  const totalCarpetArea = totalFSIArea * (carpetFactor / 100);
  
  const existingFlats = (parseFloat(formData.areaFlats) || 0) * conversionFactor;
  const existingShops = (parseFloat(formData.areaShops) || 0) * conversionFactor;
  const existingOffices = (parseFloat(formData.areaOffices) || 0) * conversionFactor;
  const existingCarpetArea = existingFlats + existingOffices + existingShops;
  const loadingPct = parseFloat(formData.loadingPercentage) || 40;
  
  const freeAreaPercent = parseFloat(competitor.additionalAreaPercent) || 0;
  const areaSharingPercent = parseFloat(competitor.areaSharePercent) || 0;
  const additionalFree = existingCarpetArea * (freeAreaPercent / 100);
  
  // Terrace/Garden converted area (same as our calculation)
  const areaTerrace = (parseFloat(formData.areaTerrace) || 0) * conversionFactor;
  const areaOverheadTerrace = (parseFloat(formData.areaOverheadTerrace) || 0) * conversionFactor;
  const areaGarden = (parseFloat(formData.areaGarden) || 0) * conversionFactor;
  const terraceConversionRatio = parseFloat(formData.terraceConversionRatio) || 25;
  const overheadTerraceConversionRatio = parseFloat(formData.overheadTerraceConversionRatio) || 25;
  const gardenConversionRatio = parseFloat(formData.gardenConversionRatio) || 25;
  const convertedTerrace = areaTerrace * (terraceConversionRatio / 100);
  const convertedOverheadTerrace = areaOverheadTerrace * (overheadTerraceConversionRatio / 100);
  const convertedGarden = areaGarden * (gardenConversionRatio / 100);
  const convertedTerraceGarden = convertedTerrace + convertedOverheadTerrace + convertedGarden;

  // Increment on converted terrace/garden (same as our calculation)
  const applyIncrementOnTG = formData.applyIncrementOnTerraceGarden === "yes";
  const incrementOnTG = applyIncrementOnTG ? convertedTerraceGarden * (freeAreaPercent / 100) : 0;
  const totalTerraceGardenWithIncrement = convertedTerraceGarden + incrementOnTG;

  // Parking compensation carpet area (same as our calculation)
  const parkingCompensationCarpet = formData.parkingCompensationType === "carpet" 
    ? (parseFloat(formData.compensationCarpetArea) || 0) * conversionFactor 
    : 0;

  // Total free area including terrace/garden and parking (matching our calculation)
  const totalFreeArea = additionalFree + totalTerraceGardenWithIncrement + parkingCompensationCarpet;
  
  let balanceCarpet, societyShare, developerShare;
  
  if (areaSharingPercent > 0) {
    societyShare = areaSharingPercent;
    developerShare = 100 - areaSharingPercent;
    balanceCarpet = (totalCarpetArea - existingCarpetArea) * (developerShare / 100);
  } else {
    balanceCarpet = Math.max(0, totalCarpetArea - existingCarpetArea - totalFreeArea);
    const totalSocietyCarpet = existingCarpetArea + totalFreeArea;
    societyShare = totalCarpetArea > 0 ? (totalSocietyCarpet / totalCarpetArea) * 100 : 0;
    developerShare = 100 - societyShare;
  }
  
  const saleableAreaSqft = (balanceCarpet * (1 + loadingPct / 100)) / conversionFactor;
  
  // Use weighted sale rate (same as our calculation)
  const saleRateFlatsVal = parseFloat(formData.saleRateFlats) || parseFloat(formData.saleRatePerSqft) || 0;
  const saleRateOfficesVal = parseFloat(formData.saleRateOffices) || saleRateFlatsVal;
  const saleRateShopsVal = parseFloat(formData.saleRateShops) || saleRateFlatsVal;
  const totalExistingArea = existingFlats + existingOffices + existingShops;
  let saleRate = saleRateFlatsVal;
  if (totalExistingArea > 0) {
    let num = 0, den = 0;
    if (existingFlats > 0) { num += existingFlats * saleRateFlatsVal; den += existingFlats; }
    if (existingOffices > 0) { num += existingOffices * saleRateOfficesVal; den += existingOffices; }
    if (existingShops > 0) { num += existingShops * saleRateShopsVal; den += existingShops; }
    if (den > 0) saleRate = num / den;
  }
  const revenue = saleableAreaSqft * saleRate;
  
  // Society expenses calculation
  const numMembers = parseInt(formData.numMembers) || 0;
  const duration = parseInt(competitor.projectDuration) || parseInt(formData.projectDuration) || 48;
  const durationYears = duration / 12;
  const existingFlatsSqft = parseFloat(formData.areaFlats) || 0;
  const existingShopsSqft = parseFloat(formData.areaShops) || 0;
  const existingOfficesSqft = parseFloat(formData.areaOffices) || 0;
  const totalExistingAreaSft = existingFlatsSqft + existingOfficesSqft + existingShopsSqft;
  
  const shiftingCharges = (parseFloat(competitor.shiftingCharges) || 0) * numMembers;
  
  // Corpus: respect formData.corpusChargesType (same as our calculation)
  let corpusCharges = 0;
  if (formData.corpusChargesType === "per_sft") {
    corpusCharges = (parseFloat(competitor.corpusCharges) || 0) * totalExistingAreaSft;
  } else {
    corpusCharges = (parseFloat(competitor.corpusCharges) || 0) * numMembers;
  }
  
  // Betterment: use competitor's own type (default per_member)
  let bettermentCharges = 0;
  const compBettermentType = competitor.bettermentChargesType || "per_member";
  if (compBettermentType === "per_sft") {
    bettermentCharges = (parseFloat(competitor.bettermentCharges) || 0) * totalExistingAreaSft;
  } else {
    bettermentCharges = (parseFloat(competitor.bettermentCharges) || 0) * numMembers;
  }
  
  // Rent calculations
  const rentFlat = parseFloat(competitor.rentPerSqftFlat) || 0;
  const rentShop = parseFloat(competitor.rentPerSqftShop) || 0;
  const rentIncrementPA = parseFloat(formData.rentIncrementPA) || 5;
  const shopRentIncrement = parseFloat(formData.shopRentIncrement) || rentIncrementPA;
  
  let totalRentFlatsOffices = 0;
  const durationYearsRent = Math.ceil(duration / 12);
  for (let year = 0; year < durationYearsRent; year++) {
    const monthsInThisYear = year === durationYearsRent - 1 ? (duration % 12 === 0 ? 12 : duration % 12) : 12;
    const yearlyMultiplier = Math.pow(1 + rentIncrementPA / 100, year);
    totalRentFlatsOffices += (existingFlatsSqft * rentFlat + existingOfficesSqft * rentFlat) * yearlyMultiplier * monthsInThisYear;
  }
  
  const shopRentDuration = parseInt(formData.shopRentDuration) || duration;
  let totalRentShops = 0;
  const shopDurationYears = Math.ceil(shopRentDuration / 12);
  for (let year = 0; year < shopDurationYears; year++) {
    const monthsInThisYear = year === shopDurationYears - 1 ? (shopRentDuration % 12 === 0 ? 12 : shopRentDuration % 12) : 12;
    const yearlyMultiplier = Math.pow(1 + shopRentIncrement / 100, year);
    totalRentShops += existingShopsSqft * rentShop * yearlyMultiplier * monthsInThisYear;
  }
  const totalRent = totalRentFlatsOffices + totalRentShops;
  
  // Brokerage (on rent - same as our calculation)
  let brokerageCost = 0;
  if (formData.brokeragePayable === "yes") {
    const brokerageMonths = parseInt(formData.brokerageMonths) || 0;
    brokerageCost = (existingFlatsSqft * rentFlat + existingOfficesSqft * rentFlat) * brokerageMonths;
  }
  
  const pmcCost = societyCosts?.pmcCost || 0;
  const bgCost = societyCosts?.bgCost || 0;
  
  // Parking compensation payout (same as our calculation)
  const parkingCompensationPayout = formData.parkingCompensationType === "payout" 
    ? (parseFloat(formData.compensationPayout) || 0) : 0;
  
  // GST — commercial (offices + shops) uses 12% − editable offset (default 5%) = 7%; flats stays at 5%
  const _cgo = parseFloat(formData.commercialGSTOffset);
  const commercialGSTOffsetPct = isNaN(_cgo) ? 5 : _cgo;
  const commercialGstRate = Math.max(0, (12 - commercialGSTOffsetPct)) / 100;
  const rrMultiplierPercent = parseFloat(formData.rrMultiplier) || 5;
  const rrMultiplierFactor = 1 + (rrMultiplierPercent / 100);
  const rrFlat = (parseFloat(formData.rrRateFlat) || 0) * rrMultiplierFactor;
  const rrShop = ((parseFloat(formData.rrRateShop) || parseFloat(formData.rrRateFlat) || 0)) * rrMultiplierFactor;
  
  const existingFlatsSqMts = existingFlatsSqft * conversionFactor;
  const existingShopsSqMts = existingShopsSqft * conversionFactor;
  const existingOfficesSqMts = existingOfficesSqft * conversionFactor;
  
  const flatFreeArea = existingFlatsSqMts * (freeAreaPercent / 100);
  const shopFreeArea = existingShopsSqMts * (freeAreaPercent / 100);
  const officeFreeArea = existingOfficesSqMts * (freeAreaPercent / 100);
  
  const gstFlats = formData.hasFlats ? (existingFlatsSqMts + flatFreeArea) * 1.1 * rrFlat * 0.05 : 0;
  const gstShops = formData.hasShops ? (existingShopsSqMts + shopFreeArea) * 1.1 * rrShop * commercialGstRate : 0;
  const gstOffices = formData.hasOffices ? (existingOfficesSqMts + officeFreeArea) * 1.1 * rrFlat * commercialGstRate : 0;
  const totalGST = gstFlats + gstShops + gstOffices;
  
  // Society expenses: deposit excluded (refundable), matching our calculation
  const societyExpenses = shiftingCharges + corpusCharges + bettermentCharges + totalRent + brokerageCost + pmcCost + bgCost + totalGST + parkingCompensationPayout;
  
  // Total expenses (same calculation as P&L Sheet / calculations.js)
  const fsiTotalCosts = fsiCosts?.totalCosts || 0;
  const totalDACharges = daChargesCosts?.totalDACharges || 0;
  const totalConstructedArea = totalCarpetArea + (totalCarpetArea * loadingPct / 100);
  const totalConstructedAreaSqft = totalConstructedArea / conversionFactor;
  const constructionCostPerSqft = parseFloat(formData.constructionCostPerSqft) || 0;
  const totalConstructionCost = totalConstructedAreaSqft * constructionCostPerSqft;
  
  // Other Costs (same as P&L Sheet - includes brokerage)
  const marketingExpenseMode = formData.marketingExpenseMode || "percent";
  let compMarketingCost = 0;
  if (marketingExpenseMode === "per_sft") {
    const marketingCostPerSqft = parseFloat(formData.marketingCostPerSqft) || 0;
    compMarketingCost = saleableAreaSqft * marketingCostPerSqft;
  } else {
    const marketingExpensePercent = parseFloat(formData.marketingExpensePercent) || 3;
    compMarketingCost = (saleableAreaSqft * saleRate) * (marketingExpensePercent / 100);
  }
  const miscExpPerSqft = parseFloat(formData.miscExpPerSqft) || 0;
  const compMiscExp = saleableAreaSqft * miscExpPerSqft;
  let compBrokerageCommission = 0;
  if (formData.brokerageCommissionType === "fixed") {
    compBrokerageCommission = parseFloat(formData.brokerageCommissionFixed) || 0;
  } else {
    const brokeragePerSqft = parseFloat(formData.brokerageCommissionPerSqft) || 0;
    compBrokerageCommission = saleableAreaSqft * brokeragePerSqft;
  }
  const otherCosts = compMarketingCost + compMiscExp + compBrokerageCommission;
  const projectInterestRate = parseFloat(formData.projectInterestRate) || 12;
  
  // Bank Interest calculation (same as P&L Sheet)
  const ancillaryFSICost = fsiCosts?.ancillaryFSICost || 0;
  const paidFSICost = fsiCosts?.paidFSICost || 0;
  const ancillaryPaidCost = ancillaryFSICost + paidFSICost;
  let interestPartABase = ancillaryPaidCost * 0.80;
  // societyExpenses here excludes PMC (will add back), matching calculations.js
  const societyExpensesExPMC = societyExpenses - pmcCost;
  const preliminaryTotalCost = fsiTotalCosts + totalDACharges + societyExpensesExPMC + totalConstructionCost + otherCosts;
  const maxInterestPartABase = Math.max(0, preliminaryTotalCost - 5000000);
  interestPartABase = Math.min(interestPartABase, maxInterestPartABase);
  const interestPartA = interestPartABase * 0.08 * durationYears;
  
  // Interest Part B: On Project Costs
  const developmentChargesDA = daChargesCosts?.developmentCharges || 0;
  const baseCostsForInterest = fsiTotalCosts + (totalConstructionCost * 0.15) + developmentChargesDA;
  const interestPartB = baseCostsForInterest * (projectInterestRate / 100) * durationYears;
  
  const bankInterest = interestPartA + interestPartB;
  
  // Total expenses: matching calculations.js formula
  // = FSI + DA + societyExpenses(excl PMC) + Construction + Other + BankInterest + PMC
  const totalExpenses = fsiTotalCosts + totalDACharges + societyExpensesExPMC + totalConstructionCost + otherCosts + bankInterest + pmcCost;
  const profit = revenue - totalExpenses;
  const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;
  
  return {
    name: competitor.name || 'Unnamed',
    freeAreaPercent,
    areaSharingPercent,
    societyShare,
    developerShare,
    saleableAreaSqft,
    revenue,
    societyExpenses: societyExpensesExPMC,
    totalExpenses,
    profit,
    profitMargin,
    duration
  };
};

// Format helpers
const formatCrores = (value) => `₹${(value / 10000000).toFixed(2)} Cr`;
const formatNumber = (value, decimals = 0) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);

// Cell with color highlighting: green = better, red = worse
const CompareCell = ({ val1, val2, higherIsBetter = true, format = "currency" }) => {
  const diff = val1 - val2;
  const isBetter = higherIsBetter ? diff > 0 : diff < 0;
  const isWorse = higherIsBetter ? diff < 0 : diff > 0;
  const color = Math.abs(diff) < 1 ? "" : isBetter ? "text-green-700 bg-green-50" : isWorse ? "text-red-600 bg-red-50" : "";
  const formatted = format === "currency" ? formatCrores(val1) : format === "number" ? formatNumber(val1) : `${val1?.toFixed(1)}%`;
  return <td className={`p-2.5 text-center font-mono text-xs ${color}`}>{formatted}</td>;
};

/**
 * Reusable comparison table for a given list of metric rows.
 * Used twice in ProjectComparison: once for all metrics, once for differences-only.
 */
function ComparisonMetricsTable({ rows, currentMetrics, compareMetrics, currentName, compareName, testId, highlight = false, readMetric }) {
  const get = readMetric || ((m, k) => m?.[k] || 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse" data-testid={testId}>
        <thead>
          <tr className="border-b-2 border-indigo-200">
            <th className="text-left p-2.5 min-w-[160px] font-semibold">Metric</th>
            <th className="text-center p-2.5 min-w-[120px] bg-green-50 font-semibold text-green-800">
              {currentName}
            </th>
            <th className="text-center p-2.5 min-w-[120px] bg-indigo-50 font-semibold text-indigo-800">
              {compareName}
            </th>
            <th className="text-center p-2.5 min-w-[100px] font-semibold">Difference</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const curVal = row.key ? get(currentMetrics, row.key) : 0;
            const cmpVal = row.key ? get(compareMetrics, row.key) : 0;
            const diff = curVal - cmpVal;
            const isBetter = row.higherIsBetter ? diff > 0 : diff < 0;
            const isWorse = row.higherIsBetter ? diff < 0 : diff > 0;
            const diffColor = Math.abs(diff) < 1 ? "text-muted-foreground" : isBetter ? "text-green-700" : isWorse ? "text-red-600" : "";
            const rowBg = row.key === "grossProfit" || row.key === "totalExpenses"
              ? "bg-accent/10 font-semibold"
              : highlight ? "bg-amber-50/40 hover:bg-amber-50/70" : "hover:bg-muted/20";
            return (
              <tr key={i} className={`border-b border-muted/30 ${rowBg}`}>
                <td className="p-2.5 text-muted-foreground">{row.label}</td>
                <td className="p-2.5 text-center font-mono">
                  {row.custom ? row.custom(currentMetrics) : row.format(curVal)}
                </td>
                <td className="p-2.5 text-center font-mono">
                  {row.custom ? row.custom(compareMetrics) : row.format(cmpVal)}
                </td>
                <td className={`p-2.5 text-center font-mono ${diffColor}`}>
                  {row.custom ? "—" : row.key === "profitMarginPercent" || row.key === "roiPercent"
                    ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)}%`
                    : row.key?.includes("Sqft") || row.key?.includes("Area")
                      ? `${diff > 0 ? '+' : ''}${formatNumber(diff, 0)}`
                      : row.key?.includes("PerSqft") || row.key?.includes("perSqft") || row.key === "constructionCostPerSqft" || row.key === "costPerSqft" || row.key === "profitPerSqft" || row.key === "saleRatePerSqft"
                        ? `${diff > 0 ? '+' : ''}₹${formatNumber(diff, 0)}`
                        : row.key === "durationYears"
                          ? `${diff > 0 ? '+' : ''}${(diff * 12).toFixed(0)} mo`
                          : `${diff > 0 ? '+' : ''}${formatCrores(diff)}`
                  }
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Project-to-Project Comparison Component
 */
function ProjectComparison({ formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, savedProjects }) {
  const [compareProjectId, setCompareProjectId] = useState("");
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCompareProject = useCallback(async (projectId) => {
    if (!projectId) { setCompareData(null); return; }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/saved-projects/${projectId}`, { withCredentials: true });
      const project = response.data;
      if (project) {
        setCompareData({
          name: project.name,
          formData: project.form_data,
          // Use saved computed_metrics directly if available (exact figures from software)
          savedMetrics: project.computed_metrics || null
        });
      }
    } catch (e) {
      console.error("Failed to load project for comparison:", e);
    }
    setLoading(false);
  }, []);

  // Current project metrics
  const currentMetrics = useMemo(() => {
    return calculateOurOfferMetrics(formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts);
  }, [formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts]);

  // Compare project metrics — use saved computed_metrics directly (exact figures from software)
  // Falls back to standalone computation only if saved metrics are unavailable
  const compareMetrics = useMemo(() => {
    if (!compareData) return null;
    // Prefer saved metrics (exact match with what software showed)
    if (compareData.savedMetrics && Object.keys(compareData.savedMetrics).length > 5) {
      return compareData.savedMetrics;
    }
    // Fallback: recompute from formData (approximate)
    if (compareData.formData) {
      return computeStandaloneProjectMetrics(compareData.formData);
    }
    return null;
  }, [compareData]);

  if (!currentMetrics) return null;

  // Helper: read a metric value, deriving for keys that aren't stored directly.
  const readMetric = (m, key) => {
    if (!m) return 0;
    if (key === "costPerSqft") {
      return m.finalSaleAreaSqft > 0 ? (m.totalExpenses || 0) / m.finalSaleAreaSqft : (m.costPerSqft || 0);
    }
    if (key === "profitPerSqft") {
      return m.finalSaleAreaSqft > 0 ? (m.grossProfit || 0) / m.finalSaleAreaSqft : (m.profitPerSqft || 0);
    }
    // Society / JV Expenses: stored WITHOUT PMC (PMC is added separately in totalExpenses)
    // For display we want the all-inclusive society spend, so add PMC back.
    if (key === "totalSocietyExpenses") {
      return (m.totalSocietyExpenses || 0) + (m.pmcCost || 0);
    }
    // ROI = Net Profit / Initial Investment x 100. Derived (not stored).
    if (key === "roiPercent") {
      const ii = m.initialInvestment || 0;
      return ii > 0 ? ((m.grossProfit || 0) / ii) * 100 : 0;
    }
    return m[key] || 0;
  };

  const rows = [
    { label: "Project Duration", key: "durationYears", format: v => `${(v * 12).toFixed(0)} months`, higherIsBetter: false },
    { label: "Total Constructed Area (sqm)", key: "totalFSIArea", format: v => formatNumber(v, 0), higherIsBetter: true },
    { label: "Total Saleable Area (sft)", key: "finalSaleAreaSqft", format: v => formatNumber(v, 0), higherIsBetter: true },
    { label: "Sharing Ratio", key: null, format: null, custom: (m) => `${m?.societySharePercent?.toFixed(0) || 0}:${m?.developerSharePercent?.toFixed(0) || 0}` },
    { label: "Sale Rate (₹/sft)", key: "saleRatePerSqft", format: v => `₹${formatNumber(v, 0)}`, higherIsBetter: true },
    { label: "Total Revenue", key: "totalRevenue", format: v => formatCrores(v), higherIsBetter: true },
    { label: "FSI & Sanction Costs", key: "fsiTotalCosts", format: v => formatCrores(v), higherIsBetter: false },
    { label: "DA Charges", key: "totalDACharges", format: v => formatCrores(v), higherIsBetter: false },
    { label: "Construction Cost", key: "totalConstructionCost", format: v => formatCrores(v), higherIsBetter: false },
    { label: "Construction ₹/sft", key: "constructionCostPerSqft", format: v => `₹${formatNumber(v, 0)}`, higherIsBetter: false },
    { label: "Society / JV Expenses", key: "totalSocietyExpenses", format: v => formatCrores(v), higherIsBetter: false },
    { label: "Other Costs", key: "totalOtherCosts", format: v => formatCrores(v), higherIsBetter: false },
    { label: "Bank Interest", key: "totalBankInterest", format: v => formatCrores(v), higherIsBetter: false },
    { label: "Total Expenses", key: "totalExpenses", format: v => formatCrores(v), higherIsBetter: false },
    { label: "Net Profit", key: "grossProfit", format: v => formatCrores(v), higherIsBetter: true },
    { label: "Profit Margin", key: "profitMarginPercent", format: v => `${v?.toFixed(1)}%`, higherIsBetter: true },
    { label: "Initial Investment", key: "initialInvestment", format: v => formatCrores(v), higherIsBetter: false },
    { label: "ROI %", key: "roiPercent", format: v => `${v?.toFixed(1)}%`, higherIsBetter: true },
    { label: "Cost per sft", key: "costPerSqft", format: v => `₹${formatNumber(v, 0)}`, higherIsBetter: false },
    { label: "Profit per sft", key: "profitPerSqft", format: v => `₹${formatNumber(v, 0)}`, higherIsBetter: true },
  ];

  // Threshold below which we treat values as identical (avoids floating-point noise).
  // Tuned per-key: percentages need a tighter threshold than crore-level amounts.
  const isSameValue = (key, curVal, cmpVal) => {
    if (key === "profitMarginPercent" || key === "roiPercent") return Math.abs(curVal - cmpVal) < 0.05; // <0.05%
    if (key === "durationYears") return Math.abs(curVal - cmpVal) < 0.01;
    if (key === "saleRatePerSqft" || key === "constructionCostPerSqft" || key === "costPerSqft" || key === "profitPerSqft") return Math.abs(curVal - cmpVal) < 1; // <₹1/sft
    return Math.abs(curVal - cmpVal) < 1; // <1 unit (sqm/sqft/rupee)
  };

  // Build "differences only" subset: rows where current and compare values differ
  const diffRows = rows.filter(row => {
    if (row.custom) {
      // For sharing ratio etc, compare formatted strings
      return row.custom(currentMetrics) !== row.custom(compareMetrics);
    }
    if (!row.key) return false;
    const curVal = readMetric(currentMetrics, row.key);
    const cmpVal = readMetric(compareMetrics, row.key);
    return !isSameValue(row.key, curVal, cmpVal);
  });

  // Format the difference cell in plain text (no JSX) for PDF rendering
  const formatDiffText = (row, curVal, cmpVal) => {
    if (row.custom) return "—";
    const diff = curVal - cmpVal;
    const sign = diff > 0 ? "+" : "";
    if (row.key === "profitMarginPercent" || row.key === "roiPercent") return `${sign}${diff.toFixed(1)}%`;
    if (row.key?.includes("Sqft") || row.key?.includes("Area")) return `${sign}${formatNumber(diff, 0)}`;
    if (row.key?.includes("PerSqft") || row.key === "constructionCostPerSqft" || row.key === "costPerSqft" || row.key === "profitPerSqft" || row.key === "saleRatePerSqft") return `${sign}Rs. ${formatNumber(diff, 0)}`;
    if (row.key === "durationYears") return `${sign}${(diff * 12).toFixed(0)} mo`;
    return `${sign}${formatCrores(diff).replace("₹", "Rs. ")}`;
  };

  const buildPDFRows = (rs) => rs.map(row => {
    const curVal = row.key ? readMetric(currentMetrics, row.key) : 0;
    const cmpVal = row.key ? readMetric(compareMetrics, row.key) : 0;
    return [
      row.label,
      row.custom ? row.custom(currentMetrics) : row.format(curVal).replace("₹", "Rs. "),
      row.custom ? row.custom(compareMetrics) : row.format(cmpVal).replace("₹", "Rs. "),
      formatDiffText(row, curVal, cmpVal),
    ];
  });

  const exportComparePDF = () => {
    if (!compareMetrics) {
      toast.error("Select a project to compare first");
      return;
    }
    const currentName = formData.projectName || "Current Project";
    const compareName = compareData?.name || "Compare Project";
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 15;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Project-to-Project Comparison", pageWidth / 2, y, { align: "center" });
    y += 8;
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`${currentName}  vs  ${compareName}`, pageWidth / 2, y, { align: "center" });
    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, pageWidth / 2, y, { align: "center" });
    doc.setTextColor(0);
    y += 8;

    // Full comparison table
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Full Comparison", 14, y);
    y += 2;
    doc.autoTable({
      startY: y + 2,
      head: [["Metric", currentName, compareName, "Difference"]],
      body: buildPDFRows(rows),
      theme: "grid",
      headStyles: { fillColor: [99, 102, 241], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "body") {
          const rowIdx = data.row.index;
          const row = rows[rowIdx];
          if (row && (row.key === "grossProfit" || row.key === "totalExpenses")) {
            data.cell.styles.fillColor = [254, 243, 199];
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Profit summary
    const curProfit = currentMetrics?.grossProfit || 0;
    const cmpProfit = compareMetrics?.grossProfit || 0;
    const better = curProfit >= cmpProfit ? currentName : compareName;
    const diffAbs = Math.abs(curProfit - cmpProfit);
    const diffPct = ((diffAbs / Math.max(Math.abs(curProfit), Math.abs(cmpProfit), 1)) * 100).toFixed(1);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(curProfit >= cmpProfit ? 21 : 185, curProfit >= cmpProfit ? 128 : 28, curProfit >= cmpProfit ? 61 : 28);
    doc.text(`${better} is more profitable by ${formatCrores(diffAbs).replace("₹","Rs. ")} (${diffPct}% difference)`, 14, y);
    doc.setTextColor(0);
    y += 8;

    // Differences-only table on a fresh page if running low on space
    if (y > 230) { doc.addPage(); y = 15; }
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    if (diffRows.length > 0) {
      doc.text(`Differences Only (${diffRows.length} of ${rows.length} metrics differ)`, 14, y);
      doc.autoTable({
        startY: y + 4,
        head: [["Metric", currentName, compareName, "Difference"]],
        body: buildPDFRows(diffRows),
        theme: "grid",
        headStyles: { fillColor: [217, 119, 6], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 8, fillColor: [255, 251, 235] },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
        },
      });
    } else {
      doc.text("Both projects have identical values across all metrics.", 14, y);
    }

    const safeName = (s) => (s || "project").replace(/[^a-z0-9]+/gi, "_").substring(0, 40);
    doc.save(`Comparison_${safeName(currentName)}_vs_${safeName(compareName)}.pdf`);
    toast.success("Comparison PDF exported");
  };

  return (
    <Card className="rounded-sm shadow-sm border-indigo-500/30" data-testid="project-comparison">
      <CardHeader className="bg-indigo-50 border-b">
        <div className="flex items-center gap-2">
          <GitCompare className="h-5 w-5 text-indigo-600" />
          <div>
            <CardTitle className="font-heading tracking-tight text-indigo-800">Project-to-Project Comparison</CardTitle>
            <CardDescription>Compare current project with another saved project variant</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Label className="text-sm whitespace-nowrap">Compare with:</Label>
          <Select value={compareProjectId} onValueChange={(val) => { setCompareProjectId(val); loadCompareProject(val); }}>
            <SelectTrigger className="rounded-sm max-w-sm" data-testid="compare-project-select">
              <SelectValue placeholder="Select a saved project..." />
            </SelectTrigger>
            <SelectContent>
              {savedProjects?.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {loading && <span className="text-xs text-muted-foreground animate-pulse">Loading...</span>}
          {compareMetrics && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={exportComparePDF}
              className="rounded-sm gap-2 ml-auto"
              data-testid="export-comparison-pdf-btn"
            >
              <Download className="h-4 w-4" />
              Export PDF
            </Button>
          )}
        </div>

        {compareMetrics && (
          <div className="space-y-6">
            {/* Helper: render a comparison table for a given set of rows */}
            {(() => null)()}
            <ComparisonMetricsTable
              rows={rows}
              currentMetrics={currentMetrics}
              compareMetrics={compareMetrics}
              currentName={formData.projectName || 'Current Project'}
              compareName={compareData?.name || 'Compare Project'}
              testId="project-compare-table"
              readMetric={readMetric}
            />

            {/* Differences-only subset */}
            {diffRows.length > 0 ? (
              <div className="border-t pt-4" data-testid="project-compare-diff-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                  <h4 className="font-semibold text-sm">
                    Differences Only ({diffRows.length} of {rows.length} metrics differ)
                  </h4>
                </div>
                <ComparisonMetricsTable
                  rows={diffRows}
                  currentMetrics={currentMetrics}
                  compareMetrics={compareMetrics}
                  currentName={formData.projectName || 'Current Project'}
                  compareName={compareData?.name || 'Compare Project'}
                  testId="project-compare-diff-table"
                  highlight
                  readMetric={readMetric}
                />
              </div>
            ) : (
              <div className="border-t pt-4 text-center py-3 text-xs text-muted-foreground bg-muted/20 rounded-sm" data-testid="project-compare-no-diff">
                Both projects have identical values across all {rows.length} metrics.
              </div>
            )}

            {/* Summary */}
            {(() => {
              const curProfit = currentMetrics?.grossProfit || 0;
              const cmpProfit = compareMetrics?.grossProfit || 0;
              const better = curProfit >= cmpProfit ? formData.projectName || "Current" : compareData?.name || "Compare";
              const diff = Math.abs(curProfit - cmpProfit);
              return (
                <div className={`p-3 rounded-sm text-xs font-medium ${curProfit >= cmpProfit ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
                  <strong>{better}</strong> is more profitable by {formatCrores(diff)} ({((diff / Math.max(Math.abs(curProfit), Math.abs(cmpProfit), 1)) * 100).toFixed(1)}% difference)
                </div>
              );
            })()}
          </div>
        )}

        {!compareMetrics && compareProjectId && !loading && (
          <p className="text-xs text-muted-foreground">Project data could not be loaded or has no form data.</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Comparison Chart Component
 */
function ComparisonChart({ formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts }) {
  // Calculate our metrics using shared function (same as P&L Sheet)
  const ourMetrics = calculateOurOfferMetrics(formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts);
  const ourSaleableAreaSqft = ourMetrics?.finalSaleAreaSqft || 0;
  const ourSocietyShare = ourMetrics?.societySharePercent || 0;
  const ourDeveloperShare = ourMetrics?.developerSharePercent || 0;
  const ourRevenue = ourMetrics?.totalRevenue || 0;
  const ourSocietyExpenses = ourMetrics?.totalSocietyExpenses || 0;
  const ourTotalExpenses = ourMetrics?.totalExpenses || 0;
  const ourProfit = ourMetrics?.grossProfit || 0;
  const ourProfitMargin = ourMetrics?.profitMarginPercent || 0;
  const ourFreeAreaPercent = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
  const saleRate = ourMetrics?.saleRatePerSqft || parseFloat(formData.saleRateFlats) || 0;
  
  // Calculate metrics for all competitors with data
  const competitorsWithData = formData.competitors
    .map((comp, idx) => ({ ...comp, index: idx }))
    .filter(c => c.additionalAreaPercent || c.areaSharePercent)
    .map(c => ({ ...calculateCompetitorMetrics(c, formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts), index: c.index }));
  
  // Find best competitor for comparison
  const bestCompetitorProfit = Math.max(...competitorsWithData.map(c => c.profit));
  const isOursBest = ourProfit >= bestCompetitorProfit;
  
  return (
    <div className="space-y-6">
      {/* Multi-Column Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="comparison-table">
          <thead>
            <tr className="border-b-2 border-primary/30">
              <th className="text-left p-3 font-semibold min-w-[180px]">Parameter</th>
              <th className="text-center p-3 font-semibold bg-green-50 text-green-700 min-w-[120px]">OUR OFFER</th>
              {competitorsWithData.map((comp) => (
                <th key={`comp-header-${comp.index}`} className="text-center p-3 font-semibold bg-amber-50 text-amber-700 min-w-[120px]">
                  {comp.name || `Competitor ${comp.index + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b hover:bg-secondary/30">
              <td className="p-3 font-medium">{formData.projectMode === "greenfield" ? "Landowner Share (%)" : "Free Area to Society (%)"}</td>
              <td className="p-3 text-center font-mono text-green-600 font-bold">{ourFreeAreaPercent}%</td>
              {competitorsWithData.map((comp) => (
                <td key={`comp-freearea-${comp.index}`} className="p-3 text-center font-mono">{comp.freeAreaPercent}%</td>
              ))}
            </tr>
            <tr className="border-b hover:bg-secondary/30 bg-secondary/10">
              <td className="p-3 font-medium">{formData.projectMode === "greenfield" ? "JV Ratio (Landowner : Dev)" : "Area Sharing (Society : Dev)"}</td>
              <td className="p-3 text-center font-mono">{ourSocietyShare.toFixed(0)}% : {ourDeveloperShare.toFixed(0)}%</td>
              {competitorsWithData.map((comp) => (
                <td key={`comp-share-${comp.index}`} className="p-3 text-center font-mono">{comp.societyShare.toFixed(0)}% : {comp.developerShare.toFixed(0)}%</td>
              ))}
            </tr>
            <tr className="border-b hover:bg-secondary/30">
              <td className="p-3 font-medium">Saleable Area (sft)</td>
              <td className="p-3 text-center font-mono text-green-600 font-bold">{formatNumber(ourSaleableAreaSqft)}</td>
              {competitorsWithData.map((comp) => (
                <td key={`comp-saleable-${comp.index}`} className={`p-3 text-center font-mono ${comp.saleableAreaSqft > ourSaleableAreaSqft ? 'text-amber-600 font-bold' : ''}`}>
                  {formatNumber(comp.saleableAreaSqft)}
                </td>
              ))}
            </tr>
            <tr className="border-b hover:bg-secondary/30 bg-secondary/10">
              <td className="p-3 font-medium">Revenue @ ₹{formatNumber(saleRate)}/sft</td>
              <td className="p-3 text-center font-mono text-green-600 font-bold">{formatCrores(ourRevenue)}</td>
              {competitorsWithData.map((comp) => (
                <td key={`comp-revenue-${comp.index}`} className={`p-3 text-center font-mono ${comp.revenue > ourRevenue ? 'text-amber-600 font-bold' : ''}`}>
                  {formatCrores(comp.revenue)}
                </td>
              ))}
            </tr>
            <tr className="border-b hover:bg-secondary/30">
              <td className="p-3 font-medium">{formData.projectMode === "greenfield" ? "JV / Project Costs" : "Society Expenses"}</td>
              <td className="p-3 text-center font-mono">{formatCrores(ourSocietyExpenses)}</td>
              {competitorsWithData.map((comp) => (
                <td key={`comp-socexp-${comp.index}`} className={`p-3 text-center font-mono ${comp.societyExpenses < ourSocietyExpenses ? 'text-amber-600 font-bold' : ''}`}>
                  {formatCrores(comp.societyExpenses)}
                </td>
              ))}
            </tr>
            <tr className="border-b hover:bg-secondary/30 bg-secondary/10">
              <td className="p-3 font-medium">Total Expenses</td>
              <td className="p-3 text-center font-mono">{formatCrores(ourTotalExpenses)}</td>
              {competitorsWithData.map((comp) => (
                <td key={`comp-totexp-${comp.index}`} className={`p-3 text-center font-mono ${comp.totalExpenses < ourTotalExpenses ? 'text-amber-600 font-bold' : ''}`}>
                  {formatCrores(comp.totalExpenses)}
                </td>
              ))}
            </tr>
            <tr className="border-b-2 border-primary/30 hover:bg-secondary/30">
              <td className="p-3 font-semibold">Profit Amount</td>
              <td className={`p-3 text-center font-mono text-lg ${isOursBest ? 'text-green-600 font-bold' : ''}`}>{formatCrores(ourProfit)}</td>
              {competitorsWithData.map((comp) => (
                <td key={`comp-profit-${comp.index}`} className={`p-3 text-center font-mono text-lg ${comp.profit === bestCompetitorProfit && !isOursBest ? 'text-amber-600 font-bold' : ''}`}>
                  {formatCrores(comp.profit)}
                </td>
              ))}
            </tr>
            <tr className="hover:bg-secondary/30 bg-secondary/10">
              <td className="p-3 font-semibold">Profit Margin (%)</td>
              <td className={`p-3 text-center font-mono text-lg ${isOursBest ? 'text-green-600 font-bold' : ''}`}>{ourProfitMargin.toFixed(1)}%</td>
              {competitorsWithData.map((comp) => (
                <td key={`comp-margin-${comp.index}`} className={`p-3 text-center font-mono text-lg ${comp.profit === bestCompetitorProfit && !isOursBest ? 'text-amber-600 font-bold' : ''}`}>
                  {comp.profitMargin.toFixed(1)}%
                </td>
              ))}
            </tr>
            <tr className="hover:bg-secondary/30">
              <td className="p-3 font-medium text-muted-foreground">Project Duration (months)</td>
              <td className="p-3 text-center font-mono">{formData.projectDuration || 48}</td>
              {competitorsWithData.map((comp) => (
                <td key={`comp-duration-${comp.index}`} className="p-3 text-center font-mono">{comp.duration}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Summary Box */}
      <div className={`p-4 rounded-sm text-center ${isOursBest ? 'bg-green-100 border border-green-300' : 'bg-amber-100 border border-amber-300'}`}>
        <p className="text-lg font-semibold">
          {isOursBest ? (
            <span className="text-green-700">✓ Our offer is the most profitable</span>
          ) : (
            <span className="text-amber-700">⚠ A competitor has a more profitable offer</span>
          )}
        </p>
        {!isOursBest && competitorsWithData.length > 0 && (
          <p className="text-sm text-muted-foreground mt-1">
            Best competitor profit: {formatCrores(bestCompetitorProfit)}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Competitor Input Card Component
 */
function CompetitorCard({ competitor, index, formData, updateCompetitor, removeCompetitor, canRemove }) {
  return (
    <div 
      className="border rounded-sm p-4 space-y-4 bg-secondary/10"
      data-testid={`competitor-card-${index}`}
    >
      <div className="flex items-center justify-between">
        <h5 className="font-semibold text-amber-700">
          Competitor {index + 1}
          {competitor.name && `: ${competitor.name}`}
        </h5>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeCompetitor(index)}
            className="text-destructive hover:text-destructive h-8 w-8 p-0"
            data-testid={`remove-competitor-${index}-btn`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Competitor Name</Label>
          <Input
            value={competitor.name}
            onChange={(e) => updateCompetitor(index, 'name', e.target.value)}
            placeholder="Enter competitor name"
            className="rounded-sm"
            data-testid={`competitor-${index}-name`}
          />
        </div>
        <div className="space-y-2">
          <Label>Project Duration (months)</Label>
          <Input
            type="number"
            value={competitor.projectDuration}
            onChange={(e) => updateCompetitor(index, 'projectDuration', e.target.value)}
            placeholder={formData.projectDuration || "e.g., 48"}
            className="rounded-sm"
            data-testid={`competitor-${index}-duration`}
          />
        </div>
      </div>
      
      <p className="text-xs text-muted-foreground font-medium">Quote to Society:</p>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Additional Free Area (%)</Label>
          <Input
            type="number"
            value={competitor.additionalAreaPercent}
            onChange={(e) => updateCompetitor(index, 'additionalAreaPercent', e.target.value)}
            placeholder="e.g., 35"
            className="rounded-sm h-9"
            data-testid={`competitor-${index}-free-area`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Area Sharing to Society (%)</Label>
          <Input
            type="number"
            value={competitor.areaSharePercent}
            onChange={(e) => updateCompetitor(index, 'areaSharePercent', e.target.value)}
            placeholder="e.g., 30"
            className="rounded-sm h-9"
            data-testid={`competitor-${index}-area-share`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Rent - Flats (₹/sft/mo)</Label>
          <Input
            type="number"
            value={competitor.rentPerSqftFlat}
            onChange={(e) => updateCompetitor(index, 'rentPerSqftFlat', e.target.value)}
            placeholder="e.g., 25"
            className="rounded-sm h-9"
            data-testid={`competitor-${index}-rent-flat`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Rent - Shops (₹/sft/mo)</Label>
          <Input
            type="number"
            value={competitor.rentPerSqftShop}
            onChange={(e) => updateCompetitor(index, 'rentPerSqftShop', e.target.value)}
            placeholder="e.g., 50"
            className="rounded-sm h-9"
            data-testid={`competitor-${index}-rent-shop`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Shifting (₹/member)</Label>
          <Input
            type="number"
            value={competitor.shiftingCharges}
            onChange={(e) => updateCompetitor(index, 'shiftingCharges', e.target.value)}
            placeholder="e.g., 50000"
            className="rounded-sm h-9"
            data-testid={`competitor-${index}-shifting`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Corpus (₹/member)</Label>
          <Input
            type="number"
            value={competitor.corpusCharges}
            onChange={(e) => updateCompetitor(index, 'corpusCharges', e.target.value)}
            placeholder="e.g., 100000"
            className="rounded-sm h-9"
            data-testid={`competitor-${index}-corpus`}
          />
        </div>
        <div className="space-y-1 col-span-2 lg:col-span-1">
          <Label className="text-xs">Betterment Charges</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={competitor.bettermentCharges}
              onChange={(e) => updateCompetitor(index, 'bettermentCharges', e.target.value)}
              placeholder="e.g., 200000"
              className="rounded-sm h-9 flex-1"
              data-testid={`competitor-${index}-betterment`}
            />
            <Select
              value={competitor.bettermentChargesType || "per_member"}
              onValueChange={(value) => updateCompetitor(index, 'bettermentChargesType', value)}
            >
              <SelectTrigger className="w-[100px] h-9 rounded-sm text-xs" data-testid={`competitor-${index}-betterment-type`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="per_member">₹/member</SelectItem>
                <SelectItem value="per_sft">₹/sft</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Main Comparison Tab Component
 */
export default function ComparisonTab({
  formData,
  setFormData,
  fsiBreakdown,
  fsiCosts,
  daChargesCosts,
  societyCosts,
  addCompetitor,
  removeCompetitor,
  updateCompetitor,
  handleTabChange,
  savedProjects
}) {
  const hasCompetitorData = formData.competitors.some(c => c.additionalAreaPercent || c.areaSharePercent);
  
  return (
    <div className="space-y-6">
      {/* Project-to-Project Comparison */}
      <ProjectComparison
        formData={formData}
        fsiBreakdown={fsiBreakdown}
        fsiCosts={fsiCosts}
        daChargesCosts={daChargesCosts}
        societyCosts={societyCosts}
        savedProjects={savedProjects}
      />

      <Card className="rounded-sm shadow-sm border-amber-500/30">
        <CardHeader className="bg-amber-500/10 border-b">
          <CardTitle className="font-heading tracking-tight text-amber-700">Competitor Comparison</CardTitle>
          <CardDescription>Compare your offer with competitor quotes and analyze profitability</CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* Project Award Status */}
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <Label>Is this project awarded?</Label>
                <RadioGroup
                  value={formData.isProjectAwarded}
                  onValueChange={(value) => setFormData({...formData, isProjectAwarded: value, awardedTo: value === "no" ? "" : formData.awardedTo})}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="comp-award-yes" />
                    <Label htmlFor="comp-award-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="comp-award-no" />
                    <Label htmlFor="comp-award-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>
              
              {formData.isProjectAwarded === "yes" && (
                <div className="space-y-3">
                  <Label>Awarded to whom?</Label>
                  <RadioGroup
                    value={formData.awardedTo}
                    onValueChange={(value) => setFormData({...formData, awardedTo: value})}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="us" id="comp-award-us" />
                      <Label htmlFor="comp-award-us" className="cursor-pointer">Awarded to Us</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="competitor" id="comp-award-competitor" />
                      <Label htmlFor="comp-award-competitor" className="cursor-pointer">Awarded to Competitor</Label>
                    </div>
                  </RadioGroup>
                </div>
              )}
            </div>
          </div>

          <Separator />
          
          {/* Multiple Competitors Input */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                Competitors ({formData.competitors.length}/5)
              </h4>
              {formData.competitors.length < 5 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCompetitor}
                  className="rounded-sm"
                  data-testid="add-competitor-btn"
                >
                  + Add Competitor
                </Button>
              )}
            </div>
            
            {formData.competitors.map((competitor, index) => (
              <CompetitorCard
                key={competitor.id || `comp-${index}`}
                competitor={competitor}
                index={index}
                formData={formData}
                updateCompetitor={updateCompetitor}
                removeCompetitor={removeCompetitor}
                canRemove={formData.competitors.length > 1}
              />
            ))}
          </div>

          {/* Comparison Chart - Only show when at least one competitor has data and FSI is calculated */}
          {hasCompetitorData && fsiBreakdown && (
            <>
              <Separator />
              <div className="bg-gradient-to-r from-primary/5 to-amber-500/5 border border-primary/20 p-6 rounded-sm">
                <h5 className="font-semibold text-lg mb-4 text-center uppercase tracking-wider">
                  Multi-Competitor Comparison Chart
                </h5>
                <ComparisonChart
                  formData={formData}
                  fsiBreakdown={fsiBreakdown}
                  fsiCosts={fsiCosts}
                  daChargesCosts={daChargesCosts}
                  societyCosts={societyCosts}
                />
              </div>
            </>
          )}

          {!fsiBreakdown && (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Complete FSI Calculation First</p>
              <p className="text-sm mt-2">Please fill in the FSI Calculator tab to enable comparison analysis</p>
            </div>
          )}

          {fsiBreakdown && !hasCompetitorData && (
            <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-sm">
              <p className="font-medium">Enter Competitor Details Above</p>
              <p className="text-sm mt-2">Fill in at least Free Area % or Area Sharing % for any competitor to see the comparison chart</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-start pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => handleTabChange("sensitivity")}
          data-testid="back-to-sensitivity-btn"
          className="rounded-sm"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back: Sensitivity
        </Button>
        <Button onClick={() => handleTabChange("analysis")} className="rounded-sm ml-auto">
          Next: Analysis <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
